import type { FunnelPageItem, FunnelRoutingEvent } from '../types';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';

const FUNNEL_PAGE_TYPE = 'funnel-page' as FunnelPageItem['type'];

/** A funnel always shows at least this many slots, padded with suggested rows. */
export const MIN_FUNNEL_ROWS = 3;

export type FunnelPageSlot =
    | { kind: 'page'; stepNumber: number; isLast: boolean; page: FunnelPageItem; children?: FunnelPageSlot[] }
    | { kind: 'suggested'; stepNumber: number; isLast: boolean };

/**
 * Lay funnel pages out by `page.order` so a page saved to order=3 stays at
 * slot #3 even if slots #1 and #2 are still empty. Pads up to MIN_FUNNEL_ROWS
 * with suggested placeholder slots. Single source of truth for both the
 * funnels list expanded view and the editor sidebar.
 */
export function buildFunnelPageSlots(pages: FunnelPageItem[]): FunnelPageSlot[] {
    const maxOrder = pages.reduce((max, p) => Math.max(max, p.order), 0);
    const totalRows = Math.max(maxOrder, MIN_FUNNEL_ROWS);
    const pageByOrder = new Map(pages.map(p => [p.order, p]));

    return Array.from({ length: totalRows }, (_, i) => {
        const stepNumber = i + 1;
        const isLast = i === totalRows - 1;
        const page = pageByOrder.get(stepNumber);
        if (page) return { kind: 'page', stepNumber, isLast, page };
        return { kind: 'suggested', stepNumber, isLast };
    });
}

/**
 * Build a nested tree of funnel pages where each page's explicit routing
 * targets (`on_purchase` / `on_skip`) are rendered as children underneath
 * their source page instead of as flat sibling rows. Pages that aren't
 * the target of any routing edge remain at the top level in order.
 *
 * Empty slots (suggested rows) are padded in by `page.order` so unfilled
 * early steps still show placeholders.
 */
export function buildFunnelPageTree(pages: FunnelPageItem[]): FunnelPageSlot[] {
    const saved = pages.filter(p => p.page_id !== DRAFT_FUNNEL_PAGE_ID);
    if (saved.length === 0) return buildFunnelPageSlots(pages);

    const byId = new Map(saved.map(p => [p.page_id, p]));

    const parentByChild = new Map<number, number>();
    // rank:
    //   0 = on_purchase (upsell branch)
    //   1 = on_skip    (downsell branch)
    //   2 = not a routed child (shouldn't appear, kept for safety)
    const childBranchRank = new Map<number, number>();
    saved.forEach(source => {
        if (!source.routing) return;
        (['on_purchase', 'on_skip'] as FunnelRoutingEvent[]).forEach(event => {
            const target = source.routing?.[event];
            if (!target) return;
            if (!byId.has(target.page_id)) return;
            if (target.page_id === source.page_id) return;
            if (parentByChild.has(target.page_id)) return;
            parentByChild.set(target.page_id, source.page_id);
            childBranchRank.set(target.page_id, event === 'on_purchase' ? 0 : 1);
        });
    });

    const makeNode = (page: FunnelPageItem): FunnelPageSlot => {
        // Sort children so on_purchase (upsell) branches come before on_skip
        // (downsell) branches, matching the visual order rendered by the nav.
        // This keeps DFS-based step numbering in sync with what the user sees.
        const directChildren = saved
            .filter(p => parentByChild.get(p.page_id) === page.page_id)
            .sort((a, b) => {
                const rankDiff = (childBranchRank.get(a.page_id) ?? 2) - (childBranchRank.get(b.page_id) ?? 2);
                if (rankDiff !== 0) return rankDiff;
                return a.order - b.order;
            });
        const children = directChildren.map((child, idx) => {
            const node = makeNode(child);
            if (node.kind === 'page') node.isLast = idx === directChildren.length - 1;
            return node;
        });
        return {
            kind: 'page',
            stepNumber: page.order,
            isLast: false,
            page,
            children: children.length > 0 ? children : undefined,
        };
    };

    const topLevelPages = saved.filter(p => !parentByChild.has(p.page_id)).sort((a, b) => a.order - b.order);
    const maxOrder = saved.reduce((max, p) => Math.max(max, p.order), 0);
    const totalRows = Math.max(maxOrder, MIN_FUNNEL_ROWS);
    const topLevelByOrder = new Map(topLevelPages.map(p => [p.order, p]));

    const slots: FunnelPageSlot[] = [];
    for (let i = 0; i < totalRows; i++) {
        const stepNumber = i + 1;
        const page = topLevelByOrder.get(stepNumber);
        if (page) {
            slots.push(makeNode(page));
        } else if (!saved.some(p => p.order === stepNumber)) {
            slots.push({ kind: 'suggested', stepNumber, isLast: false });
        }
    }

    const last = slots[slots.length - 1];
    if (last) last.isLast = true;

    return slots;
}

export function isFunnelStepComplete(pages: FunnelPageItem[], stepNumber: number): boolean {
    const page = pages.find(p => p.order === stepNumber);
    if (!page) return false;
    if (page.type !== FUNNEL_PAGE_TYPE) return true;
    return typeof page.referencePageId === 'number';
}

export function arePreviousStepsComplete(pages: FunnelPageItem[], stepNumber: number): boolean {
    for (let step = 1; step < stepNumber; step++) {
        if (!isFunnelStepComplete(pages, step)) return false;
    }
    return true;
}

/**
 * Walk a funnel page tree depth-first and return a map from page_id → sequential
 * step number so nested branches count toward the total page count.
 */
export function buildFunnelPageTreePositions(slots: FunnelPageSlot[]): Map<number, number> {
    const map = new Map<number, number>();
    let counter = 0;
    const walk = (slot: FunnelPageSlot) => {
        if (slot.kind !== 'page') return;
        counter += 1;
        map.set(slot.page.page_id, counter);
        (slot.children ?? []).forEach(walk);
    };
    slots.forEach(walk);
    return map;
}
